import { toast } from 'sonner';
import { convertImagesIfNeeded } from './convert-image';
import { matchesAccept } from './file-types';

/** Standard limit — images and misc files (20 MB). */
export const MAX_UPLOAD_SIZE = 20 * 1024 * 1024;
export const MAX_UPLOAD_SIZE_LABEL = '20 MB';

/** Heavy-file limit — video + print docs (PDF/EPS/AI, e.g. brand guides) (200 MB). */
export const MAX_LARGE_FILE_SIZE = 200 * 1024 * 1024;
export const MAX_LARGE_FILE_SIZE_LABEL = '200 MB';

/** PDF / EPS / AI / PPT(X) — brand guides and print files that are legitimately large. */
const LARGE_DOC_RE = /\.(pdf|eps|ai|ppt|pptx)$/i;

/**
 * Resolves the size ceiling for a file. Video and large document formats
 * (PDF/EPS/AI) get the bigger allowance; images and everything else use the
 * standard limit.
 */
function limitFor(file: File): { max: number; label: string } {
	const type = file.type.toLowerCase();
	const isHeavy =
		type.startsWith('video/') ||
		type === 'application/pdf' ||
		type === 'application/postscript' ||
		LARGE_DOC_RE.test(file.name);
	if (isHeavy) {
		return { max: MAX_LARGE_FILE_SIZE, label: MAX_LARGE_FILE_SIZE_LABEL };
	}
	return { max: MAX_UPLOAD_SIZE, label: MAX_UPLOAD_SIZE_LABEL };
}

/**
 * Validates files against the per-type size limit and (optionally) an `accept`
 * string, surfacing a toast that names any rejected file(s). Returns only the
 * files that pass, so callers never silently swallow an oversized or
 * wrong-type upload. Enforcing here covers drag-drop and raw inputs, not just
 * the file picker's soft `accept` hint.
 */
export function filterFiles(files: FileList | File[], accept?: string): File[] {
	const accepted: File[] = [];
	const oversized: { name: string; label: string }[] = [];
	const wrongType: string[] = [];

	for (const file of Array.from(files)) {
		const { max, label } = limitFor(file);
		if (file.size > max) oversized.push({ name: file.name, label });
		else if (accept && !matchesAccept(file, accept)) wrongType.push(file.name);
		else accepted.push(file);
	}

	if (oversized.length === 1) {
		toast.error(`"${oversized[0].name}" exceeds the ${oversized[0].label} limit`);
	} else if (oversized.length > 1) {
		toast.error(`${oversized.length} files exceed the size limit`);
	}

	if (wrongType.length === 1) {
		toast.error(`"${wrongType[0]}" is not a supported file type`);
	} else if (wrongType.length > 1) {
		toast.error(`${wrongType.length} files are not a supported file type`);
	}

	return accepted;
}

/**
 * The single ingestion entry point: converts HEIC/HEIF to JPEG, then validates
 * size + type via {@link filterFiles}. Route EVERY upload path through this —
 * file picker, drag-drop and raw `<input>` — so conversion and validation
 * behave identically everywhere, not just the FileInput browse path.
 */
export async function prepareFiles(files: FileList | File[], accept?: string): Promise<File[]> {
	const converted = await convertImagesIfNeeded(Array.from(files));
	return filterFiles(converted, accept);
}
